import React, { useMemo, useRef } from 'react';
import { BatteryHistoryItem } from '../../../types/api';

interface ChartDataPoint {
  x: number;
  y: number | null;
}

interface UseBatteryChartProps {
  data: BatteryHistoryItem[];
}

interface UseBatteryChartReturn {
  processedChartData: {
    charging: ChartDataPoint[];
    discharging: ChartDataPoint[];
    temperature: ChartDataPoint[];
    remainingCharging: ChartDataPoint[];
    remainingDischarging: ChartDataPoint[];
  };
  chartRef: React.RefObject<any>;
}

export const useBatteryChart = ({ data }: UseBatteryChartProps): UseBatteryChartReturn => {
  const chartRef = useRef<any>(null);

  const processedChartData = useMemo(() => {
    const toChartPoints = (items: BatteryHistoryItem[], getValue: (item: BatteryHistoryItem) => number | null): ChartDataPoint[] => {
      return items
        .map(item => ({
          x: typeof item.measured_at === 'number' ? item.measured_at * 1000 : new Date(item.measured_at).getTime(),
          y: getValue(item)
        }))
        .filter(point => point.y !== null)
        .sort((a, b) => a.x - b.x);
    };

    return {
      charging: toChartPoints(
        data.filter(item => item.charging_status === 'charging'),
        item => item.battery_level
      ),
      discharging: toChartPoints(
        data.filter(item => item.charging_status === 'discharging'),
        item => item.battery_level
      ),
      temperature: toChartPoints(
        data.filter(item => item.temperature_celsius != null),
        item => item.temperature_celsius!
      ),
      remainingCharging: toChartPoints(
        data.filter(item => item.remaining_time_minutes != null && item.charging_status === 'charging'),
        item => item.remaining_time_minutes!
      ),
      remainingDischarging: toChartPoints(
        data.filter(item => item.remaining_time_minutes != null && item.charging_status === 'discharging'),
        item => item.remaining_time_minutes!
      ),
    };
  }, [data]);

  return {
    processedChartData,
    chartRef,
  };
};